import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { safeUrl } from '../utils/safeUrl';
import { isAdminLike, isSuperAdmin } from '../utils/roles';
import { siblingPageUrl } from '../utils/compareLink';
import { formatEventDateTime } from '../utils/dates';
import { buildChartData, availableSectionIdsFrom, chartGroupsFrom } from '../utils/chartData';
import { bulkUpdatePrices, getEventPricing } from '../api';
import { useEventPricing, eventPricingKeys } from '../hooks/useEventPricing';
import { useVenueData } from '../hooks/useVenueData';
import { useDebouncedValue } from '../hooks/useDebouncedValue';
import { inventoryKeys } from '../hooks/useInventory';
import { useLPricePoll } from '../hooks/useLPricePoll';
import { useLadderWrite } from '../hooks/useLadderWrite';
import {
  lionAutoKeys,
  useLionAutoGroups,
  useLionAutoGroupMatches,
  useLionAutoPreview,
  useLionAutoLadder,
  useCreateLionAutoGroup,
  useUpdateLionAutoGroup,
  useDeleteLionAutoGroup,
  useReviewLionAutoPending,
  useLionAutoStatus,
  useRunLionAutoGroup,
  useLionAutoFloors,
  useLionAutoSaleRefill,
} from '../hooks/useLionAuto';
import CompareSeatMap from '../components/CompareSeatMap';
import LaGroupList, { MAX_GROUPS } from '../components/lionAuto/LaGroupList';
import LaGroupEditor from '../components/lionAuto/LaGroupEditor';
import LaAutoPanel from '../components/lionAuto/LaAutoPanel';
import LaFloorsPanel from '../components/lionAuto/LaFloorsPanel';
import LaSaleRefillPanel from '../components/lionAuto/LaSaleRefillPanel';
import LaListingsTable from '../components/lionAuto/LaListingsTable';
import LaLadderBar, { LADDER_CHANGED } from '../components/lionAuto/LaLadderBar';
import { retryAfterSec, detailLines, listingLabel } from '../components/lionAuto/LaMatchResults';
import LaCriteriaLegend from '../components/lionAuto/LaCriteriaLegend';
import {
  NEW_GROUP_CRITERIA, MAX_SECTIONS, withCriteriaDefaults, cleanCriteria, duplicateCriteria,
} from '../components/lionAuto/LaCriteriaEditor';
import {
  nextGroupName, copyGroupName, groupsBySectionFrom, hasExactSections, listingGroupsFrom, draftGroupName,
  hoveredGroupCss, SAFE_ID,
} from '../utils/lionAutoGroups';
import {
  toggleLadderId, cleanLadderIds, withoutLadder, ladderResume, mergeLadder, lTrackingFor, ladderKeyFor,
  ladderEndAction, insertLadderId, rankOf,
} from '../utils/lionAutoLadder';
import {
  knownFloorPct, autoSwitchDefault, postSaveSteps, shouldTurnAutoOn, autoOnErrorText, postSaveText, smartRankedCount,
} from '../utils/lionAutoSave';
import { approveWhereText } from '../utils/lionAutoPending';
import { writeBlockReason, writeLadderChunks, writeOutcomeText } from '../utils/lionAutoWrite';
import { rowOptionsFrom } from '../utils/lionAutoRows';
import {
  effectiveFloor, inheritedFloorFor, floorsBlockedReason, ladderFloorChanged, ladderFloorChangedText,
} from '../utils/lionAutoFloors';
import {
  toggleInclude, toggleExclude, toggleExcludedComp, withLiveOverrides,
} from '../utils/lionAutoOverrides';
import { holdOf, holdNeedsPrice } from '../utils/lionAutoHold';
import {
  isDryRun, isEnabled, autoGroupsStale, repricedCount, describeRunError, needsAutoLogin,
} from '../utils/lionAutoRunner';

// Lion Auto: Compare's sibling for automation. The table is Compare's: all
// our listings and the whole market, and map / zone clicks filter it to the
// sections picked. A pricing group is such a selection saved: our listings in
// its sections that pass its Qty, rows and public-notes filters are its
// members by themselves (our cheapest = anchor) and the market listings there
// that pass them are its comps. Picking a group selects its sections and lays
// its match result over the table. A section can be in several groups (e.g.
// close rows and back rows of the same sections, split by the rows ticked in
// the Rows filter, or limited-view and full-view seats, split by the
// public-notes filter; Duplicate is the quick way to make the second one),
// but a listing of ours is in one group per event: the group that first held
// it keeps it, and the others show it as "In another group". Group saves
// land in our database; with a floor, Save then prices the group (below).
// Price ladder: while a group is edited the pricer clicks its listings in
// the table in rank order (saved with the group). Calculate prices asks the
// server for each rank's B — rank 1's L $offset below the anchor comp, each
// next rank $step above the one before, every L Skybox's own what-if; with
// the group's floor (a % of each listing's own cost), a rank whose target
// falls under its floor is priced at the floor ("held by floor") and the
// ranks above step up from there; with holding on, only ranks 1..holdLive
// are laddered and the later ones sit at one hold price, a B, until a live
// one sells and the ranks close up. It needs a floor (the group's, the
// show's or the artist's). Once the run is complete (Continues included)
// the page writes every new B at once, with no review step, through the
// guarded bulk-price path Compare's Apply uses (useLadderWrite); a run that
// stopped early writes nothing. After it the L converges the way it does on
// Compare: Skybox's what-if L is pinned at once and useLPricePoll checks
// Vivid until it has the new price.
// Save group: saves, then (with a floor) prices the saved group the same
// way and, for an admin, turns Auto on after that write (utils/lionAutoSave).
// Auto: a saved group with a floor is on Auto (on by an admin, off by anyone
// who edits it: unticking the editor's box); the server then re-ladders and
// applies it on its own. The page turns it on after a save and off with
// one, and shows the last run on the group's card. Admins also get the runner's
// panel (master switch, dry run, cadence, next runs) and "Run now" per group
// on Auto; the runner's status, while read, re-reads the groups when it
// shows a run (or a pause) the cached cards don't have yet.

// Sections in a saved group are tinted on the map by a page-level <style>
// keyed on the base SVG's data-sid (the way CompareSeatMap greys out empty
// sections), so the shared map component needs no Lion Auto prop. Same color
// as .la-swatch-other in styles.css.
const OTHER_GROUP_FILL = '#c9b8f2';
// The group card under the mouse lights its sections the same way. Same
// colors as .la-swatch-hover in styles.css.
const HOVER_GROUP_FILL = '#67e8f9';
const HOVER_GROUP_STROKE = '#0891b2';
const NO_RESULTS = {};
// After a price write that timed out, fresh reads at these delays show which
// prices landed (the server may still be writing; Compare's +8 s / +45 s)
const RECHECK_AFTER_MS = [8000, 45000];
// Continue calls a Calculate chains by itself (each prices ~20 rungs in the
// server's 45 s budget); past it the pricer clicks Continue
const MAX_AUTO_CONTINUES = 12;
// Calculate writes, so it never runs without a floor
const NO_FLOOR_BLOCKED = 'Set a floor first (group, show or artist): Calculate writes prices';
// A Save group's pricing that got no ladder answer back
const NO_LADDER_ANSWER = 'the ladder did not answer (too many requests, or the group changed): Calculate prices again';
// How long a Save group's last toast stays up
const POST_SAVE_TOAST_MS = 5000;
const POST_SAVE_ERROR_TOAST_MS = 9000;
// A ladder write that fell short: the toast stays up as long
const WRITE_ERROR_TOAST_MS = 9000;

// What makes two drafts "the same" for the unsaved-changes prompt
const draftSig = (d) => JSON.stringify({
  name: (d.name || '').trim(),
  criteria: cleanCriteria(d.criteria),
  autoEnabled: !!d.autoEnabled,
});

// A selection entry, the shape group criteria store sections in
const sectionEntry = (sectionId, name) => ({
  sectionId: Number(sectionId),
  name: String(name || `Section ${sectionId}`).slice(0, 80),
});

// Adds the given sections the list lacks, or — when it has them all —
// removes them (a zone click toggles its sections as a whole). secs:
// sectionEntry objects.
function toggleSections(current, secs) {
  const have = new Set(current.map(s => Number(s.sectionId)));
  if (secs.every(s => have.has(s.sectionId))) {
    const drop = new Set(secs.map(s => s.sectionId));
    return current.filter(s => !drop.has(Number(s.sectionId)));
  }
  const next = [...current];
  for (const s of secs) {
    if (!have.has(s.sectionId)) { next.push(s); have.add(s.sectionId); }
  }
  return next;
}

function LionAuto({ user }) {
  const { eventId } = useParams();
  const [searchParams] = useSearchParams();
  const queryClient = useQueryClient();

  // Header comes from the same query params as Compare
  const eventName = searchParams.get('name') || 'Event';
  const eventDate = searchParams.get('date');
  const venue = searchParams.get('venue');
  const venueCity = searchParams.get('city');
  const venueState = searchParams.get('state');
  // Scheme-checked: a javascript: href from the query string would run in-origin
  const vividSeatsUrl = safeUrl(searchParams.get('vividUrl'));
  const compareHref = siblingPageUrl('/compare', eventId, searchParams);

  // ---- toasts ----
  const [toast, setToast] = useState(null); // { kind: 'success'|'error', text }
  const toastTimerRef = useRef(null);
  const showToast = useCallback((kind, text, ms = 4000) => {
    clearTimeout(toastTimerRef.current);
    setToast({ kind, text });
    toastTimerRef.current = setTimeout(() => setToast(null), ms);
  }, []);
  useEffect(() => () => clearTimeout(toastTimerRef.current), []);

  // ---- data ----
  const {
    data: pricing,
    isLoading: pricingLoading,
    error: pricingError,
    refetch: refetchPricing,
  } = useEventPricing(eventId);
  const { data: venueData, isLoading: mapLoading } = useVenueData(eventId);

  // L-price convergence after a write, shared with Compare: Skybox's what-if
  // L is pinned for a B we just wrote, and a slim /lprices poll on a bounded
  // backoff patches only the L fields into the pricing cache until Vivid has
  // the price (or flags it when Vivid never takes it). Re-armed by every
  // full payload (mount, Refresh comps, someone else's write), so a priced
  // row with no L converges here too.
  const lPoll = useLPricePoll(eventId);
  const {
    rearmFrom: lPollRearm, overlay: lOverlay, verifyState: lVerifyState,
    pendingIds: lPendingIds, staleIds: lStaleIds, pollNow: lPollNow,
  } = lPoll;
  useEffect(() => {
    if (pricing?.myInventory) lPollRearm(pricing.myInventory);
  }, [pricing, lPollRearm]);
  // Our rows as the table shows them (Compare's mineWithConfirmedPrices): a
  // pinned L — Skybox's what-if for a B we just wrote, or the last value the
  // poller confirmed — over a cached row still carrying the old or no L, plus
  // the poller's state for the L cell. The poller keeps its state in refs;
  // pendingIds / staleIds change with it, so they key this memo.
  const tablePricing = useMemo(() => {
    if (!pricing?.myInventory) return pricing;
    const myInventory = pricing.myInventory.map((row) => {
      const id = String(row.id);
      const { unitPrice, pinned } = lOverlay(row);
      const tracked = lPendingIds.has(id);
      if (!pinned && !tracked) return row;
      const lVerify = tracked || lStaleIds.has(id) ? lVerifyState(row.id) : null;
      return { ...row, unitPrice, lPricePinned: pinned, lTracked: tracked, lVerify };
    });
    return { ...pricing, myInventory };
  }, [pricing, lOverlay, lVerifyState, lPendingIds, lStaleIds]);
  const groupsQ = useLionAutoGroups(eventId);
  const groups = useMemo(() => groupsQ.data?.groups || [], [groupsQ.data]);
  // Section id → every group holding it (the server's sectionMembership is
  // not read: the groups' own criteria stay right after a cache patch)
  const groupsBySection = useMemo(() => groupsBySectionFrom(groups), [groups]);
  const matchesQ = useLionAutoGroupMatches(eventId, groups);
  const results = matchesQ.data?.results || NO_RESULTS;
  // The floor a group with none of its own uses (this show's, else the
  // artist's), for the cards, the editor and the ladder bar; null while
  // unknown (none on a server without floors). The Floors box reads the same
  // cached query.
  const floorsQ = useLionAutoFloors(eventId);
  const inheritedFloor = inheritedFloorFor(floorsQ);
  // This event's Reprice after a sale (its show's, else its artist's, else the
  // runner's default), for the editor's Auto warning; every staff role reads
  // it (the Reprice after a sale box reads the same cached query). null
  // while unknown.
  const saleRefillQ = useLionAutoSaleRefill(eventId);
  const saleRefill = saleRefillQ.data?.effective?.on ?? null;

  // ---- Auto runner (admins) ----
  const adminLike = isAdminLike(user?.role);
  // Read once here (the panel polls it while open); never for pricers
  const autoStatusQ = useLionAutoStatus({ enabled: adminLike });
  const autoStatus = adminLike ? autoStatusQ.data || null : null;
  // A status showing this event's groups ahead of the cached cards (a run,
  // a pause) re-reads the groups: once per new status payload
  const groupsRef = useRef(groups);
  groupsRef.current = groups;
  const groupsLoaded = !!groupsQ.data;
  useEffect(() => {
    if (!groupsLoaded || !autoStatus) return;
    if (autoGroupsStale(groupsRef.current, autoStatus.groups, eventId)) {
      queryClient.invalidateQueries({ queryKey: lionAutoKeys.groups(eventId) });
    }
  }, [autoStatus, groupsLoaded, eventId, queryClient]);
  // Group id → next run, for the cards (this event's groups on Auto)
  const autoDue = useMemo(() => {
    if (!autoStatus || !Array.isArray(autoStatus.groups)) return null;
    const out = {};
    for (const g of autoStatus.groups) {
      if (g && String(g.eventId) === String(eventId)) out[String(g.id)] = g.nextDueAt ?? null;
    }
    return out;
  }, [autoStatus, eventId]);
  const autoRunner = useMemo(
    () => (autoStatus ? { cadence: autoStatus.config?.cadence ?? null, dryRun: isDryRun(autoStatus) } : null),
    [autoStatus]
  );
  // The server refuses a run while the master switch is off (409 AUTO_DISABLED)
  const autoRunBlocked = autoStatus && !isEnabled(autoStatus)
    ? 'The Auto runner is off: turn it on in the Auto runner panel first'
    : null;
  // Group id → its last "Run now" on this page: { summary, group, error, at }
  const [autoRuns, setAutoRuns] = useState({});
  // Our listings by id, to name them in a dry run's would-write list
  const ourListingNames = useMemo(
    () => new Map((pricing?.myInventory || []).map(l => [String(l.id), listingLabel(l)])),
    [pricing]
  );
  const listingName = useCallback((id) => ourListingNames.get(String(id)) || null, [ourListingNames]);

  // ---- selection + draft ----
  // freeSelection is Compare's section filter: [{ sectionId, name }], empty =
  // the whole venue. While a draft is open, its sections are the selection.
  const [freeSelection, setFreeSelection] = useState([]);
  // The group last picked (card click, save, edit, duplicate): it is on
  // screen while the selection is still exactly its sections
  const [shownGroupId, setShownGroupId] = useState(null);
  // The group card under the mouse (its sections light on the map), or null
  const [hoveredGroupId, setHoveredGroupId] = useState(null);
  // null | { id, name, criteria, autoEnabled?, savedAuto? }: savedAuto is the
  // group's Auto switch when the draft opened (a new group has none)
  const [draft, setDraft] = useState(null);
  const [draftOrigSig, setDraftOrigSig] = useState(null);
  const [saveError, setSaveError] = useState(null); // string[] | null
  const [draftNotice, setDraftNotice] = useState(null); // string | null
  // The editor's advanced filters start open (a duplicate) or folded
  const [draftAdvancedOpen, setDraftAdvancedOpen] = useState(false);
  // The table's "Show my other listings" while a draft is open: off for
  // every draft opened, and read only while editing
  const [showAllOwn, setShowAllOwn] = useState(false);

  // ---- price ladder state ----
  // The last Calculate (with any Continue merged in): { key, data,
  // receivedAt, error? } or { key, error }, shown only while `key` is still
  // the ladder on screen (see ladderKey below)
  const [ladder, setLadder] = useState(null);
  const recheckTimersRef = useRef([]);
  // The ladder's price write and its last outcome (the bar's banner). The
  // handlers are defined further down; the arrows read them when called.
  const ladderWrite = useLadderWrite({
    onApplied: (outcome) => handleLadderApplied(outcome),
    onOutcomeUnknown: () => recheckAfterTimeout(),
  });
  const resetWrite = ladderWrite.reset;
  // A Save group still pricing its group (see "Save group: price it, then
  // Auto on"): null | { groupId, name, steps, autoOff, phase, key }
  const [postSave, setPostSave] = useState(null);

  // Another event starts clean: no draft, no answer, no Save still pricing,
  // no Continue chain, and no write banner or lock (forced: the last event's
  // rechecks are cancelled below, so its lock would never be released; a
  // write still running keeps "writing" and lands as idle)
  useEffect(() => {
    setDraft(null);
    setDraftOrigSig(null);
    setFreeSelection([]);
    setShownGroupId(null);
    setSaveError(null);
    setDraftNotice(null);
    setLadder(null);
    setPostSave(null);
    resetWrite({ force: true });
    autoRef.current = { armed: false, key: null, count: 0, lastAfter: null, waiting: null, answered: false };
    setAutoRuns({});
    return () => {
      recheckTimersRef.current.forEach(clearTimeout);
      recheckTimersRef.current = [];
    };
    // autoRef is a ref (declared with the ladder below); resetWrite is stable
  }, [eventId, resetWrite]);

  const editing = !!draft;
  // The saved group being edited; null for a new group, or browsing
  const currentGroupId = editing ? draft.id : null;
  const selection = editing ? draft.criteria.sections : freeSelection;
  // Gold on the map and the table's filter. The map keys sections by string id.
  const mapSections = useMemo(
    () => selection.map(s => ({ id: String(s.sectionId), name: s.name })),
    [selection]
  );
  const mapSectionIds = useMemo(() => new Set(mapSections.map(s => s.id)), [mapSections]);
  // The Rows filter's dropdown: every row of ours and the market's in the
  // draft's sections, with counts; null while the listings load. Keyed on
  // the payload and the sections only, so other edits don't rebuild it.
  const rowOptions = useMemo(
    () => (editing && pricing ? rowOptionsFrom(pricing, mapSections) : null),
    [editing, pricing, mapSections]
  );

  // The group on screen is the one last picked, while the selection is still
  // exactly its sections: any other click changes the set and the overlay
  // goes by itself. Groups may share sections, so it is never guessed from
  // the selection — the same sections picked by hand show no group.
  const freeIds = useMemo(() => new Set(freeSelection.map(s => String(s.sectionId))), [freeSelection]);
  const selectedGroup = useMemo(() => {
    const g = shownGroupId == null ? null : groups.find(x => x.id === shownGroupId);
    return g && hasExactSections(g, freeIds) ? g : null;
  }, [groups, shownGroupId, freeIds]);
  const shownGroup = editing ? null : selectedGroup;
  const savedResult = shownGroup ? results[String(shownGroup.id)] || null : null;

  // Live preview: only the matching criteria drive it (renaming, ranking
  // listings or any other ladder setting costs no call). Memoized on their
  // JSON, so a rank click keeps the same input: the debounce settles and the
  // table doesn't flash "updating". The edited group's id goes with them
  // (none for a new group or a duplicate) and is debounced with them, so
  // opening another group never asks for its id with the last draft's
  // criteria. A listing is in one group per event, so the preview also
  // depends on the other saved groups (useLionAutoPreview keys on them).
  const draftCriteria = draft?.criteria;
  const draftMatchJson = useMemo(
    () => (draftCriteria ? JSON.stringify(withoutLadder(cleanCriteria(draftCriteria))) : null),
    [draftCriteria]
  );
  const draftInput = useMemo(
    () => (draftMatchJson ? { criteria: JSON.parse(draftMatchJson), groupId: currentGroupId } : null),
    [draftMatchJson, currentGroupId]
  );
  const debouncedInput = useDebouncedValue(draftInput, 400);
  const preview = useLionAutoPreview(eventId, debouncedInput, groups);
  const draftHasSections = !!draft && draft.criteria.sections.length > 0;
  const previewResult = draftHasSections ? preview.data || null : null;

  const activeResult = draft ? previewResult : savedResult;
  const draftDirty = !!draft && draftSig(draft) !== draftOrigSig;

  // Our listing → the groups taking it (the table's Group column): one at
  // most from this server, which gives a listing to one group per event; an
  // older server could name two, which the table flags. Every saved group's
  // members; the draft's live preview stands in for the group being edited,
  // or joins as the new group. The draft's name is debounced so typing it
  // doesn't re-render the table on every key.
  const draftName = useDebouncedValue(draft ? draft.name : null, 400);
  const previewMembers = previewResult?.members || null;
  const groupMembership = useMemo(
    () => listingGroupsFrom(groups, results, editing ? { id: currentGroupId, name: draftName, members: previewMembers } : null),
    [groups, results, editing, currentGroupId, draftName, previewMembers]
  );
  // The table's Group column: the name the group on screen goes under in
  // that map (its chip the stronger), and "…" until the matches are in
  const currentGroupName = editing
    ? draftGroupName(groups, { id: currentGroupId, name: draftName })
    : shownGroup?.name ?? null;
  const groupsLoading = groupsQ.isLoading || matchesQ.isLoading;

  // A 429 on the preview: say how long to wait; the next edit asks again
  const previewError = preview.error;
  useEffect(() => {
    if (previewError?.response?.status === 429) {
      showToast('error', `Too many match requests — the preview pauses for ${retryAfterSec(previewError)} s.`, 6000);
    }
  }, [previewError, showToast]);

  // ---- map ----
  const svgContent = venueData?.svg || null;
  const chartData = useMemo(() => buildChartData(venueData), [venueData]);
  const availableSectionIds = useMemo(() => availableSectionIdsFrom(pricing, chartData), [pricing, chartData]);
  const zones = useMemo(() => chartGroupsFrom(chartData, pricing?.sectionGroups), [chartData, pricing]);

  // Purple = in a saved group, for information only: any section can join
  // any group. While group G is edited, only the other groups count (G's
  // sections are the draft's own); a new draft and browsing count them all.
  // Selected sections stay gold, never under a group's purple.
  const otherGroupsCss = useMemo(() => {
    const selectors = [];
    for (const [sid, owners] of groupsBySection) {
      if (mapSectionIds.has(sid) || !SAFE_ID.test(sid)) continue;
      if (!owners.some(o => o.groupId !== currentGroupId)) continue;
      selectors.push(`.la-page .compare-map-section .base-layer path[data-sid="${sid}"]`);
    }
    return selectors.length ? `${selectors.join(',\n')}{fill:${OTHER_GROUP_FILL}}` : '';
  }, [groupsBySection, currentGroupId, mapSectionIds]);

  // Cyan = the hovered card's sections, drawn after the purple (so it wins
  // there); gold selections stay on top. Looked up on every render, so a
  // deleted group lights nothing; the group being edited is skipped (its
  // sections are the draft's own, already gold).
  const hoveredGroup = hoveredGroupId == null || hoveredGroupId === draft?.id
    ? null
    : groups.find(g => g.id === hoveredGroupId) || null;
  const hoverCss = useMemo(
    () => hoveredGroupCss(hoveredGroup, {
      fill: HOVER_GROUP_FILL, stroke: HOVER_GROUP_STROKE, selectorPrefix: '.la-page .compare-map-section .base-layer',
    }),
    [hoveredGroup]
  );

  // The map's click handler stays stable (the map is memoized), so it reads
  // the draft through a ref
  const draftRef = useRef(draft);
  draftRef.current = draft;

  const toggleDraftSections = useCallback((secs) => {
    setDraft(d => {
      if (!d || !secs.length) return d;
      const sections = toggleSections(d.criteria.sections, secs).slice(0, MAX_SECTIONS)
        .map(s => sectionEntry(s.sectionId, s.name));
      return { ...d, criteria: { ...d.criteria, sections } };
    });
  }, []);
  const toggleFreeSections = useCallback((secs) => {
    if (secs.length) setFreeSelection(cur => toggleSections(cur, secs));
  }, []);

  // A map click filters the table as on Compare (browsing) or adds / removes
  // a draft section (editing): any section, whatever groups hold it.
  // Chart section ids are Skybox section ids; chart zone ids are not Skybox
  // group ids, so zones never leave the page — only their sections do.
  const handleMapToggle = useCallback((sec) => {
    const id = Number(sec?.id);
    if (!Number.isInteger(id) || id <= 0) return;
    const secs = [sectionEntry(id, sec.name)];
    if (draftRef.current) toggleDraftSections(secs);
    else toggleFreeSections(secs);
  }, [toggleDraftSections, toggleFreeSections]);

  // A zone click toggles all its sections, browsing or editing
  const handleZoneToggle = (zone) => {
    const secs = zone.sections
      .map(s => sectionEntry(s.id, s.name))
      .filter(s => Number.isInteger(s.sectionId) && s.sectionId > 0);
    if (draft) toggleDraftSections(secs);
    else toggleFreeSections(secs);
  };

  const clearSelection = () => {
    if (draft) setDraft(d => (d ? { ...d, criteria: { ...d.criteria, sections: [] } } : d));
    else setFreeSelection([]);
  };

  // ---- group actions ----
  const createM = useCreateLionAutoGroup(eventId);
  const updateM = useUpdateLionAutoGroup(eventId);
  const deleteM = useDeleteLionAutoGroup(eventId);
  const saving = createM.isPending || updateM.isPending;

  const confirmDiscard = () => !draftDirty || window.confirm('Discard your unsaved changes to this group?');

  // A draft opened starts with no write banner (a new draft shares the
  // "new group" scope with the last one)
  const openDraft = (d, notice = null, advancedOpen = false) => {
    setDraft(d);
    setDraftOrigSig(draftSig(d));
    setSaveError(null);
    setDraftNotice(notice);
    setDraftAdvancedOpen(advancedOpen);
    setShowAllOwn(false);
    resetWrite();
  };

  // A new group starts from the whole selection (sections already in other
  // groups included). A selection that is the group on screen starts it
  // empty: Duplicate is the way to copy a group.
  const startNew = () => {
    if (draft && !confirmDiscard()) return;
    const seed = selectedGroup ? [] : freeSelection;
    openDraft({ id: null, name: nextGroupName(groups), criteria: { ...NEW_GROUP_CRITERIA, sections: seed.slice(0, MAX_SECTIONS) } });
  };

  // Selects the group's sections underneath a draft opened from it, so
  // Cancel leaves that group on screen
  const showGroupUnderDraft = (g, sections) => {
    setFreeSelection(sections.map(s => sectionEntry(s.sectionId, s.name)));
    setShownGroupId(g.id);
  };

  const startEdit = (g) => {
    if (draft?.id === g.id) return;
    if (draft && !confirmDiscard()) return;
    const criteria = withCriteriaDefaults(g.criteria);
    showGroupUnderDraft(g, criteria.sections);
    // A group saved before groups were built from sections comes back with
    // none: it matches nothing until the pricer picks them. An admin's Auto
    // box opens ticked on a group that is off (Save turns Auto on;
    // unticking keeps it off), unticked on one the runner paused.
    openDraft(
      { id: g.id, name: g.name || '', criteria, autoEnabled: autoSwitchDefault(g, adminLike), savedAuto: !!g.autoEnabled },
      criteria.sections.length ? null : 'This group has no sections yet. Click sections on the map or a zone, then save.'
    );
  };

  // A new, unsaved group with the saved group's sections and filters (never
  // its id), advanced filters open: the second group on the same sections is
  // usually the first with other rows ticked (close rows vs back rows) or
  // another Public notes filter. It keeps the picked rows like any filter.
  // Its ladder keeps the offset, step, floor and holding but starts unranked: its
  // members are other listings. Saved without ranking any by hand, the server
  // ranks its members in Smart sort order (as for any new group); ranked by
  // hand, it keeps that order. Never its Auto switch: a new group starts
  // off (the server ignores it on create too). Nor the listings it included
  // or excluded by hand: those were picks among the original's listings. A
  // listing stays with the group that holds it, so the copy starts with none
  // of the original's: the notice says to narrow the original to split them.
  // The comps it took out by hand stay (excludeCompIds): comps are shared, not
  // held by one group (duplicateCriteria).
  const startDuplicate = (g) => {
    if (draft && !confirmDiscard()) return;
    const criteria = duplicateCriteria(g.criteria);
    showGroupUnderDraft(g, criteria.sections);
    openDraft(
      { id: null, name: copyGroupName(groups, g.name), criteria, autoEnabled: false, savedAuto: false },
      `Copy of ‘${g.name}’ — a listing is in one group only, so ‘${g.name}’ keeps every listing it takes and the copy gets the rest: narrow ‘${g.name}’ with a filter (e.g. Rows or Public notes) to split them. The copy starts with none of your listings included or excluded by hand. Save when ready.`,
      true
    );
  };

  const cancelEdit = () => {
    if (!confirmDiscard()) return;
    setDraft(null);
    setSaveError(null);
    setDraftNotice(null);
  };

  // A group card selects the group's sections; its result overlays the
  // table. Another group drops the last write's banner (kept while locked).
  const selectGroup = (id) => {
    if (draft) {
      if (draft.id === id) return;
      if (!confirmDiscard()) return;
      setDraft(null);
      setSaveError(null);
    }
    if (shownGroup?.id !== id) resetWrite();
    const g = groups.find(x => x.id === id);
    setFreeSelection((g?.criteria?.sections || []).map(s => sectionEntry(s.sectionId, s.name)));
    setShownGroupId(g ? g.id : null);
  };

  const patchDraft = (patch) => setDraft(d => (d ? { ...d, ...patch } : d));

  const handleSaveError = (err) => {
    const status = err?.response?.status;
    const data = err?.response?.data || {};
    if (status === 429) {
      showToast('error', `Too many requests — try saving again in ${retryAfterSec(err)} s.`, 6000);
      return;
    }
    if (status === 400) {
      setSaveError([data.error || 'The group was not accepted.', ...detailLines(data.details)]);
      return;
    }
    if (status === 403) {
      setSaveError([data.code === 'AUTO_ADMIN_ONLY' ? 'Only an admin can turn Auto on.' : 'You do not have access to this event.']);
      return;
    }
    // A new group sent unranked is ranked from the event's Skybox pricing;
    // with none (or a 503, below) nothing is saved
    if (status === 404 && data.code === 'PRICING_NOT_FOUND') {
      setSaveError(['Skybox has no pricing for this event, so the new group could not be ranked and was not saved.']);
      return;
    }
    if (status === 404) {
      setSaveError([data.code === 'NOT_FOUND'
        ? 'This group no longer exists — someone may have deleted it. Cancel to drop your changes.'
        : 'Lion Auto is not available on this server yet.']);
      return;
    }
    setSaveError([data.error || err?.message || 'Save failed.']);
  };

  const handleSave = () => {
    if (!draft || saving || postSave) return;
    if (!draft.criteria.sections.length) {
      setSaveError(['Pick at least one section on the map or a zone first.']);
      return;
    }
    // Listings sold since they were included / excluded by hand drop out of
    // those lists (ownIds: our listings now), as they do on a toggle
    const body = { name: draft.name.trim(), criteria: withLiveOverrides(cleanCriteria(draft.criteria), ownIds) };
    // Unticking Auto on a group on Auto goes with the save (only when flipped
    // in this edit, so a draft opened before someone else flipped it never
    // flips it back). Auto goes ON only after the save priced the group.
    const autoOff = draft.id != null && !!draft.savedAuto && !draft.autoEnabled;
    if (autoOff) body.autoEnabled = false;
    const savedId = draft.id;
    const before = savedId != null ? groups.find(g => g.id === savedId) || null : null;
    // A group the runner paused goes back on only when someone ticked it
    const paused = !!before && !before.autoEnabled && before.autoLastStatus === 'paused' && !draft.autoEnabled;
    const callbacks = {
      // The groups cache now holds the server's copy (normalized criteria);
      // select its sections, make it the group on screen (its overlay comes
      // up), drop the draft, then price it (startPostSave). A new group sent
      // unranked comes back ranked in Smart sort order (smartRankedCount)
      onSuccess: (data) => {
        setDraft(null);
        setSaveError(null);
        setDraftNotice(null);
        const criteria = data?.group?.criteria || body.criteria;
        const saved = criteria.sections;
        setFreeSelection((Array.isArray(saved) ? saved : body.criteria.sections).map(s => sectionEntry(s.sectionId, s.name)));
        const groupId = data?.group?.id ?? savedId;
        setShownGroupId(groupId);
        startPostSave({
          groupId,
          name: body.name,
          autoOff,
          smartRanked: smartRankedCount({ savedId, sentIds: body.criteria.ladderIds, savedIds: criteria.ladderIds }),
          steps: postSaveSteps({
            floorPct: knownFloorPct(criteria.ladderFloorPct, inheritedFloor),
            rankedCount: Array.isArray(criteria.ladderIds) ? criteria.ladderIds.length : 0,
            adminLike,
            savedAuto: !!draft.savedAuto,
            autoExplicitlyOff: savedId != null && !draft.autoEnabled,
            paused,
          }),
        });
      },
      onError: handleSaveError,
    };
    if (draft.id != null) updateM.mutate({ id: draft.id, ...body }, callbacks);
    else createM.mutate(body, callbacks);
  };

  const handleDelete = (g) => {
    if (!window.confirm(`Delete group “${g.name}”? Nothing changes in Skybox.`)) return;
    deleteM.mutate(g.id, {
      onSuccess: () => {
        if (draft?.id === g.id) { setDraft(null); setSaveError(null); setDraftNotice(null); }
        showToast('success', `Deleted “${g.name}”`, 2500);
      },
      onError: (err) => {
        const status = err?.response?.status;
        if (status === 429) showToast('error', `Too many requests — try again in ${retryAfterSec(err)} s.`, 6000);
        else if (status === 404) showToast('error', 'That group was already deleted.');
        else if (status === 403) showToast('error', 'You do not have access to this event.');
        else showToast('error', err?.response?.data?.error || 'Delete failed.');
      },
    });
  };

  // ---- needs approval ----
  // A card's listings that joined after the group was built: Approve (smart)
  // slots each in at its Smart sort spot (read from the event's Skybox
  // pricing: a 404 PRICING_NOT_FOUND or 503 approves nothing), At rank… at a
  // typed rank (beforeId; 409 RANKING_CHANGED if it left the ladder; neither
  // for the last rank: ranked last), Leave unranked clears the flag only
  // (utils/lionAutoPending). The groups cache takes the server's copy, the
  // matches re-read (useReviewLionAutoPending), and the ladder answer on
  // screen goes by itself (ladderKey holds the ranking). Approving into a
  // group on Auto asks first: the runner prices them next.
  const reviewM = useReviewLionAutoPending(eventId);
  const handleReviewPending = (g, action, inventoryIds, { smart = false, beforeId = null, rank = null, memberIds = null } = {}) => {
    const ids = Array.isArray(inventoryIds) ? inventoryIds : [];
    if (!ids.length || reviewM.isPending) return;
    const listings = (n) => `${n} listing${n === 1 ? '' : 's'}`;
    const sent = { smart, beforeId, rank };
    if (action === 'approve' && g.autoEnabled && !window.confirm(
      `Approve ${listings(ids.length)} in “${g.name}”? Auto is on for this group: the server will reprice ` +
      `them on its next run (${approveWhereText({ ...sent, count: ids.length })}; never under the floor).`
    )) return;
    reviewM.mutate({ id: g.id, action, inventoryIds: ids, beforeId, smart }, {
      onSuccess: (data) => {
        const skipped = Array.isArray(data?.skipped) ? data.skipped.length : 0;
        const done = action === 'approve' ? data?.approved : data?.dismissed;
        const n = Array.isArray(done) ? done.length : Math.max(0, ids.length - skipped);
        if (!n) {
          showToast('error', 'Someone already handled those listings.');
          return;
        }
        const handled = skipped ? ` — ${skipped} ${skipped === 1 ? 'was' : 'were'} already handled` : '';
        // One listing: the rank it landed on, as the card numbered them
        const landed = memberIds && n === 1 && ids.length === 1 ? rankOf(data?.group?.criteria?.ladderIds, memberIds, ids[0]) : null;
        const at = approveWhereText({ ...sent, count: n }, { landed, placedLast: data?.placedLast });
        showToast('success', action === 'approve'
          ? `Approved ${listings(n)} in “${g.name}” — ${at}${handled}`
          : `Left ${listings(n)} unranked${handled}`, 4000);
      },
      onError: (err) => {
        const status = err?.response?.status;
        const data = err?.response?.data || {};
        const details = detailLines(data.details);
        if (status === 429) showToast('error', `Too many requests — try again in ${retryAfterSec(err)} s.`, 6000);
        else if (status === 409 && data.code === 'NOT_PENDING') showToast('error', 'Someone already handled those listings.');
        else if (status === 409 && data.code === 'RANKING_CHANGED') showToast('error', 'The ranking changed since you looked. Check the ranks and try again.', 6000);
        else if (status === 404 && data.code === 'PRICING_NOT_FOUND') showToast('error', 'Skybox has no pricing for this event, so nothing was approved.', 6000);
        else if (status === 404) showToast('error', data.code === 'NOT_FOUND' ? 'That group was deleted.' : 'Approvals are not available on this server yet.');
        else if (status === 403) showToast('error', 'You do not have access to this event.');
        else if (status === 400) showToast('error', `${data.error || 'The server did not accept that.'}${details.length ? ` (${details.join('; ')})` : ''}`, 6000);
        else showToast('error', data.error || (action === 'approve' ? 'Approve failed.' : 'Leave unranked failed.'));
      },
    });
  };

  // ---- Auto: Run now ----
  // One run of a group on Auto through the runner's own path (dry run while
  // the runner is in dry run). Live — or not known to be dry — asks first.
  // A live run's prices land like a ladder write's minus the per-row answer: the
  // inventory list re-reads, and the table's prices follow on Refresh comps
  // (an immediate pricing read could catch Skybox mid re-publish).
  const runAutoM = useRunLionAutoGroup(eventId);
  const handleRunAuto = (g) => {
    if (runAutoM.isPending) return;
    const live = autoStatus ? !isDryRun(autoStatus) : null;
    if (live !== false) {
      const ask = live
        ? `Run Auto now for “${g.name}”? The runner is LIVE: the new prices go to Skybox at once, with nobody reviewing them (never under the floor).`
        : `Run Auto now for “${g.name}”? If the runner is live, the new prices go to Skybox at once, with nobody reviewing them.`;
      if (!window.confirm(ask)) return;
    }
    const key = String(g.id);
    runAutoM.mutate(g.id, {
      onSuccess: (data) => {
        const summary = data?.summary || null;
        setAutoRuns(r => ({ ...r, [key]: { summary, group: data?.group || null, error: null, at: Date.now() } }));
        const n = repricedCount(summary);
        if (summary && !summary.dryRun && n > 0) {
          queryClient.invalidateQueries({ queryKey: inventoryKeys.all });
          showToast('success', `Auto repriced ${n} listing${n === 1 ? '' : 's'} in “${g.name}” — Refresh comps to see the new prices.`, 6000);
        }
      },
      onError: (err) => {
        const error = err?.response?.status === 429
          ? `Too many requests — try again in ${retryAfterSec(err)} s.`
          : describeRunError(err);
        // needsLogin: the card links to Admin → Data, where the login is set up
        setAutoRuns(r => ({ ...r, [key]: { summary: null, group: null, error, needsLogin: needsAutoLogin(err), at: Date.now() } }));
      },
    });
  };
  const dismissAutoRun = useCallback((id) => {
    setAutoRuns(r => {
      const next = { ...r };
      delete next[String(id)];
      return next;
    });
  }, []);

  // ---- refresh ----
  // A genuinely fresh Skybox read (server floor 10 s), exactly like Compare's
  // Refresh, then re-match against it. The match routes read the same server
  // cache, so they pick the fresh payload up.
  const refreshPricing = useCallback(async () => {
    try {
      await queryClient.fetchQuery({
        queryKey: eventPricingKeys.detail(eventId),
        queryFn: async () => (await getEventPricing(eventId, { params: { maxAge: 10000 }, timeout: 200000 })).data,
        staleTime: 0,
      });
    } catch (_) {
      // Surfaces through the pricing query's own error state
    } finally {
      queryClient.invalidateQueries({ queryKey: lionAutoKeys.matches(eventId) });
      queryClient.invalidateQueries({ queryKey: lionAutoKeys.preview(eventId) });
    }
  }, [queryClient, eventId]);

  // The button: the pricer wants Skybox's numbers now, so a ladder answer
  // computed from the old comps goes, and any L still pending is checked
  // right away (as on Compare)
  const [refreshing, setRefreshing] = useState(false);
  const handleRefresh = async () => {
    if (refreshing) return;
    setRefreshing(true);
    setLadder(null);
    lPollNow();
    try {
      await refreshPricing();
    } finally {
      setRefreshing(false);
    }
  };

  // ---- price ladder ----
  // The ladder on screen is the draft's, or the saved group's on screen. Its
  // key is all the server prices from: the group (its criteria stand in for
  // that group's among the event's groups; none = a new group) and the full
  // cleaned criteria — ranking, offset, step, floor, sections and filters —
  // plus the floor it prices at (its own, else the show's or the artist's),
  // so any change to them, a new floor in the Floors box included, drops the
  // answer (ladderKeyFor; the server reads only groupId and criteria).
  const ladderGroupId = draft ? draft.id : shownGroup?.id ?? null;
  const ladderSource = draft ? draft.criteria : shownGroup?.criteria || null;
  const ladderFloor = effectiveFloor(ladderSource?.ladderFloorPct, ladderSource ? inheritedFloor : null);
  const ladderFloorPct = ladderFloor.pct;
  const ladderKey = useMemo(
    () => (ladderSource
      ? ladderKeyFor({ groupId: ladderGroupId, criteria: cleanCriteria(ladderSource), floorPct: ladderFloorPct })
      : null),
    [ladderSource, ladderGroupId, ladderFloorPct]
  );
  const ladderSpec = useMemo(() => (ladderKey ? JSON.parse(ladderKey) : null), [ladderKey]);
  const ladderKeyRef = useRef(ladderKey);
  ladderKeyRef.current = ladderKey;
  useEffect(() => {
    setLadder(l => (l && l.key !== ladderKey ? null : l));
  }, [ladderKey]);
  const shownLadder = ladder && ladder.key === ladderKey ? ladder : null;
  const ladderM = useLionAutoLadder(eventId);

  // A rank click in the table (draft only): next rank, or out of the ladder
  const toggleRank = useCallback((inventoryId) => {
    setDraft(d => {
      if (!d) return d;
      const ladderIds = toggleLadderId(d.criteria.ladderIds, inventoryId);
      return ladderIds === d.criteria.ladderIds ? d : { ...d, criteria: { ...d.criteria, ladderIds } };
    });
  }, []);
  // A typed rank in the table (draft only): in just before beforeId (null = last)
  const insertRank = useCallback((inventoryId, beforeId) => setDraft(d => {
    const ladderIds = d ? insertLadderId(d.criteria.ladderIds, inventoryId, beforeId) : null;
    return !d || ladderIds === d.criteria.ladderIds ? d : { ...d, criteria: { ...d.criteria, ladderIds } };
  }), []);
  // Auto rank in the table (draft only): the order shown replaces the
  // ranking, after asking when there is one. Only the unsaved draft changes;
  // the ladder answer drops as after a rank click.
  const setRanks = useCallback((inventoryIds) => {
    const ladderIds = cleanLadderIds(inventoryIds);
    if (!ladderIds.length) return;
    const current = ladderSpec ? ladderSpec.criteria.ladderIds.length : 0;
    if (current > 0 && !window.confirm(
      `Replace the current ${current} rank${current === 1 ? '' : 's'} with the order shown?`
    )) return;
    setDraft(d => d && { ...d, criteria: { ...d.criteria, ladderIds } });
    showToast('success', `Ranked ${ladderIds.length} listing${ladderIds.length === 1 ? '' : 's'} in table order`, 3000);
  }, [ladderSpec, showToast]);
  // The table's Rank column: a draft ranks by clicking (Auto rank, a typed rank); a
  // saved group shows its order read-only. With holding on (holdLive > 0)
  // each rank is tagged live or held: ranks 1..holdLive live, and later ones
  // too until the live ones hold holdSeats seats (the Live qty, 0 = off).
  const ranking = useMemo(
    () => (ladderSpec
      ? {
        ids: ladderSpec.criteria.ladderIds,
        holdLive: ladderSpec.criteria.holdLive,
        holdSeats: ladderSpec.criteria.holdSeats,
        onToggle: editing ? toggleRank : null,
        onSet: editing ? setRanks : null,
        onInsert: editing ? insertRank : null,
      }
      : null),
    [ladderSpec, editing, toggleRank, setRanks, insertRank]
  );
  // Holding as the ladder prices it ({ live, seats, price }), null when off
  const ladderHold = ladderSpec ? holdOf(ladderSpec.criteria) : null;
  const rankedCount = ladderSpec ? ladderSpec.criteria.ladderIds.length : 0;
  // An Include / Exclude click in the table (draft only): the listing goes
  // in or out of the group by hand (excluding also unranks it), and ids no
  // longer among our listings (sold) drop out of both lists
  const ownIds = useMemo(
    () => (pricing?.myInventory ? new Set(pricing.myInventory.map(l => String(l.id))) : null),
    [pricing]
  );
  const toggleOverride = useCallback((inventoryId, mode) => {
    const toggle = mode === 'include' ? toggleInclude : mode === 'exclude' ? toggleExclude : null;
    if (!toggle) return;
    setDraft(d => {
      if (!d) return d;
      const criteria = toggle(d.criteria, inventoryId, ownIds);
      return criteria === d.criteria ? d : { ...d, criteria };
    });
  }, [ownIds]);
  // A Not a comp / Use as comp click in the table (draft only): the market
  // listing leaves the group's comps by hand, or comes back (by Vivid id; our
  // listings sold never touch it)
  const toggleCompOverride = useCallback((vividListingId) => setDraft(d => {
    if (!d) return d;
    const criteria = toggleExcludedComp(d.criteria, vividListingId);
    return criteria === d.criteria ? d : { ...d, criteria };
  }), []);
  // The table's overrides: a draft toggles them; a saved group's read-only.
  // Memoized on the three lists' JSON, so a rank click (a new ladderSpec)
  // doesn't re-filter the table.
  const overridesJson = ladderSpec
    ? JSON.stringify([ladderSpec.criteria.includeIds, ladderSpec.criteria.excludeIds, ladderSpec.criteria.excludeCompIds])
    : null;
  const overrides = useMemo(() => {
    if (!overridesJson) return null;
    const [includeIds, excludeIds, excludeCompIds] = JSON.parse(overridesJson);
    return {
      includeIds,
      excludeIds,
      excludeCompIds,
      onToggle: editing ? toggleOverride : null,
      onToggleComp: editing ? toggleCompOverride : null,
    };
  }, [overridesJson, editing, toggleOverride, toggleCompOverride]);

  // One call at a time; it may belong to another group picked since (its
  // variables are the spec — whose JSON is the key — plus a resume when it
  // continues a run). The bar only shows with a ranking, and the server
  // refuses a draft with no sections.
  const pendingLadder = ladderM.isPending ? ladderM.variables : null;
  const runningHere = !!pendingLadder && ladderKeyFor(pendingLadder) === ladderKey;
  // One price write at a time, and none while the last one is locked (a
  // timed-out write not re-read yet, a 429 still counting down): never re-sent
  const writeBlock = writeBlockReason(ladderWrite);
  let ladderBlocked = null;
  if (pendingLadder && !runningHere) ladderBlocked = 'Another ladder is still calculating';
  else if (writeBlock) ladderBlocked = writeBlock;
  else if (draft && !draft.criteria.sections.length) ladderBlocked = 'Pick at least one section first';
  // The server refuses holding with no hold price
  else if (holdNeedsPrice(ladderSpec?.criteria)) ladderBlocked = 'Set a hold price, or turn holding off';
  // The floor it prices at must be known first, and set: Calculate writes
  else ladderBlocked = floorsBlockedReason(floorsQ) || (ladderFloorPct > 0 ? null : NO_FLOOR_BLOCKED);
  // The last write's outcome, shown while its group's ladder is on screen
  const writeScope = `${eventId}:${ladderGroupId ?? 'new'}`;
  const shownWrite = ladderWrite.scope === writeScope ? ladderWrite : null;

  // An answer the server priced at another floor than ours (our floors copy
  // was behind): never written nor continued; the bar says Calculate again
  const ladderData = shownLadder?.data || null;
  const ladderFloorStale = ladderFloorChanged(ladderData, ladderFloorPct);
  // Where the answer on screen can be continued (it stopped early), or null
  const ladderResumeAt = useMemo(
    () => (ladderFloorStale ? null : ladderResume(ladderData)),
    [ladderData, ladderFloorStale]
  );

  // onAnswer(key, data) stores an answer; one for criteria changed meanwhile
  // is dropped. A failed Calculate replaces the answer, a failed Continue
  // keeps the rungs priced so far. The chain that launched the call (see
  // below) waits for it to settle before it may end, and notes that it got
  // an answer of its own.
  const runLadder = (body, keepData, onAnswer) => {
    if (!ladderSpec || ladderBlocked || ladderM.isPending) return;
    const key = ladderKey;
    const chain = autoRef.current;
    const launch = {};
    chain.waiting = launch;
    const settle = () => { if (chain.waiting === launch) chain.waiting = null; };
    ladderM.mutate(body, {
      onSuccess: (data) => {
        settle();
        // Priced at another floor than we expected: re-read the floors (the
        // new key then drops this answer)
        if (ladderFloorChanged(data, body.floorPct)) queryClient.invalidateQueries({ queryKey: lionAutoKeys.floors(eventId) });
        if (ladderKeyRef.current === key) {
          onAnswer(key, data);
          chain.answered = true;
        }
      },
      onError: (err) => {
        settle();
        if (err?.response?.status === 429) {
          showToast('error', `Too many ladder requests — try again in ${retryAfterSec(err)} s.`, 6000);
          return;
        }
        if (ladderKeyRef.current !== key) return;
        setLadder(l => (keepData && l?.key === key && l.data ? { ...l, error: err } : { key, error: err }));
      },
    });
  };
  // A run that stops at the server's time budget carries on by itself: each
  // answer that can be continued launches the next Continue, up to
  // MAX_AUTO_CONTINUES calls, and stops at an error, a what-if failure or an
  // answer that made no progress. Armed by Calculate / Continue for the
  // criteria it ran on (key); lastAfter = the rank the last launch resumed
  // after; waiting = its call in flight (set at once, before isPending shows
  // it); answered = a call of this chain stored an answer. When the chain
  // ends, endChain writes a complete answer.
  const autoRef = useRef({ armed: false, key: null, count: 0, lastAfter: null, waiting: null, answered: false });
  const arm = () => { autoRef.current = { armed: true, key: ladderKey, count: 0, lastAfter: null, waiting: null, answered: false }; };
  // A new Calculate clears the last write's banner. Nothing at all while
  // blocked (a locked write keeps its banner and countdown).
  const calculateLadder = () => {
    if (!ladderSpec || ladderBlocked || ladderM.isPending) return;
    resetWrite();
    arm();
    runLadder(ladderSpec, false, (key, data) => setLadder({ key, data, receivedAt: Date.now() }));
  };
  const calculateLadderRef = useRef(calculateLadder);
  calculateLadderRef.current = calculateLadder;
  // Same criteria, from where the run stopped; the new rungs merge into the
  // answer on screen (the write, once the run is complete, covers every rank)
  const continueRun = () => {
    if (!ladderResumeAt) return;
    const { afterRank, prevL } = ladderResumeAt;
    runLadder({ ...ladderSpec, resume: { afterRank, prevL } }, true, (key, data) => setLadder((l) => {
      if (!l || l.key !== key || !l.data) return l;
      const merged = mergeLadder(l.data, data, afterRank);
      return merged ? { ...l, data: merged, error: null } : { ...l, error: LADDER_CHANGED };
    }));
  };
  const continueLadder = () => {
    arm();
    continueRun();
  };
  const continueRunRef = useRef(continueRun);
  continueRunRef.current = continueRun;
  // The end of a chain: a complete answer it got itself, for the criteria it
  // ran on, is written at once (ladderEndAction); anything else writes
  // nothing and keeps its banners (Continue / Calculate again). A Save group
  // pricing this run (postSave) hears how it ended, after the write.
  const endChain = (chain) => {
    const own = chain.answered && chain.key === ladderKey && !!ladderData && !shownLadder?.error;
    const action = own ? ladderEndAction(ladderData, { resume: ladderResumeAt, floorStale: ladderFloorStale }) : 'incomplete';
    const job = postSave?.phase === 'pricing' && postSave.key === chain.key ? postSave : null;
    if (action === 'unchanged' && own) {
      // Nothing to write, but listings already at their ladder price still get
      // the first-time `lion` tag: a best-effort tag-only request (writeLadderChunks
      // sends no price and never rejects) that the page does not wait for
      writeLadderChunks(ladderData.rungs, bulkUpdatePrices).catch((err) => console.error('[Lion Auto] tag-only request', err));
    }
    // Only 'write' writes: 'stepDown' (no comp, the step-down's preview) is the Auto runner's to write
    if (action !== 'write') {
      // No answer and no error in the bar (a 429 is only a toast, or the group changed)
      const priced = own || shownLadder?.error ? { action, result: null } : { action: 'blocked', reason: NO_LADDER_ANSWER };
      if (job) finishPostSave(job, priced);
      return;
    }
    if (job) setPostSave(p => (p === job ? { ...p, phase: 'writing' } : p));
    // A Save hears the outcome; a plain Calculate gets an error toast for
    // anything short of every row written (the success toast is
    // handleLadderApplied's). run never rejects (null = another write was
    // running); the catch is for a bug in the handling, so a Save still ends.
    // all rungs: writeLadderChunks sends the OK ones as prices and the UNCHANGED ones' ids for the tag
    ladderWrite.run(ladderData.rungs, { scope: writeScope })
      .then((result) => {
        if (job) {
          finishPostSave(job, { action, result });
          return;
        }
        const outcome = writeOutcomeText(result);
        if (outcome.bad) showToast('error', `Calculate prices: ${outcome.text}`, WRITE_ERROR_TOAST_MS);
      })
      .catch((err) => {
        console.error('[Lion Auto] ladder write end', err);
        if (job) finishPostSave(job, { action: 'write', result: null });
        else showToast('error', `Calculate prices: ${writeOutcomeText(null).text}`, WRITE_ERROR_TOAST_MS);
      });
  };
  const endChainRef = useRef(endChain);
  endChainRef.current = endChain;
  // endChainRef / continueRunRef are refs (always the latest render's
  // functions), so they stay out of the deps
  useEffect(() => {
    const auto = autoRef.current;
    if (!auto.armed) return;
    // Its call still in flight (isPending lags a render behind the launch)
    if (ladderM.isPending || auto.waiting) return;
    if (auto.key !== ladderKey || !shownLadder?.data || !ladderResumeAt) {
      // Done, or nothing to continue from: the chain ends. (An error on a
      // failed Calculate has no data either.)
      auto.armed = false;
      endChainRef.current(auto);
      return;
    }
    const stopped = shownLadder.error
      || ladderBlocked
      || (ladderData.status || []).includes('WHATIF_FAILED')
      || auto.count >= MAX_AUTO_CONTINUES
      || auto.lastAfter === ladderResumeAt.afterRank;
    if (stopped) {
      auto.armed = false;
      endChainRef.current(auto);
      return;
    }
    auto.count += 1;
    auto.lastAfter = ladderResumeAt.afterRank;
    continueRunRef.current();
  }, [ladderM.isPending, shownLadder, ladderResumeAt, ladderData, ladderBlocked, ladderKey]);
  // Pricing the rest on its own (the bar words its banner for it)
  const autoContinuing = autoRef.current.armed && autoRef.current.key === ladderKey
    && !!ladderResumeAt && !shownLadder?.error;

  // Prices landed (a ladder write: Calculate's or Save group's), handled like
  // Compare's handleBulkApplied: the new B and Skybox's what-if L go into the
  // pricing cache in place, the what-if is pinned, and the poller follows
  // Vivid — rows new to Vivid until their first L, rows already there until
  // Vivid's L moves. No pricing refetch now: the write cleared the server's
  // pricing cache, so a read this early could catch Skybox mid re-publish
  // (old or $0 L) and keep it for minutes; the poller's patches and the next
  // Refresh comps bring the rest. For the same reason the group matches and
  // the preview are only marked stale (they re-match against that server
  // cache): a price write changes no membership, and the overlay re-reads on
  // Refresh comps or the next edit. The ladder answer goes — it was priced
  // from the old Bs.
  const handleLadderApplied = ({ results: written = [], errors = [] }) => {
    if (!written.length) return;
    const before = new Map((queryClient.getQueryData(eventPricingKeys.detail(eventId))?.myInventory || [])
      .map(item => [String(item.id), item]));
    const byId = new Map(written.map(r => [String(r.inventoryId), r]));
    queryClient.setQueryData(eventPricingKeys.detail(eventId), (prev) => {
      if (!prev?.myInventory) return prev;
      return {
        ...prev,
        myInventory: prev.myInventory.map((item) => {
          const r = byId.get(String(item.id));
          if (!r) return item;
          return {
            ...item,
            listPrice: r.newPrice,
            ...(r.unitPrice != null ? { unitPrice: r.unitPrice } : {}),
            lastPriceUpdate: new Date().toISOString(),
          };
        }),
      };
    });
    queryClient.invalidateQueries({ queryKey: inventoryKeys.all });
    queryClient.invalidateQueries({ queryKey: lionAutoKeys.matches(eventId), refetchType: 'none' });
    queryClient.invalidateQueries({ queryKey: lionAutoKeys.preview(eventId), refetchType: 'none' });
    const { pins, write, verify } = lTrackingFor(written, before);
    for (const p of pins) lPoll.pin(p.id, p.unitPrice, p.listPrice);
    if (write.length) lPoll.track(write);
    if (verify.length) lPoll.track(verify, { origin: 'verify' });
    setLadder(null);
    const n = written.length;
    const tracking = write.length + verify.length > 0;
    showToast(errors.length ? 'error' : 'success', `Applied ${n} price${n === 1 ? '' : 's'}${errors.length ? `, ${errors.length} failed` : ''}${tracking ? ' — L updates on Vivid shortly.' : '.'}`, 5000);
  };

  // A write timed out: the server may still be writing, so read fresh prices
  // twice; the table then shows which ones landed. Calculate stays blocked
  // (the write is locked) until the last read has landed, failed or not
  // (refreshPricing never rejects: a failed read shows on the pricing query).
  const unlockUnknownWrite = ladderWrite.unlockUnknown;
  const recheckAfterTimeout = () => {
    RECHECK_AFTER_MS.forEach((ms, i) => {
      const last = i === RECHECK_AFTER_MS.length - 1;
      recheckTimersRef.current.push(setTimeout(() => {
        const read = refreshPricing();
        if (last) read.finally(unlockUnknownWrite);
      }, ms));
    });
  };

  // ---- Save group: price it, then Auto on ----
  // After the DB save (postSaveSteps): with a floor and ranks, the saved
  // group's ladder is calculated — once it is on screen and no other ladder
  // or write is running — and endChain writes it like any Calculate. Auto
  // goes on after that write only when it fully landed (or every rank
  // already matched: shouldTurnAutoOn), or right after the save when
  // nothing is ranked. One toast tells it all, Auto staying off included.
  // phase: waiting (for the group on screen) → pricing (its chain, key) →
  // writing.
  const showPostSaveToast = (job, priced, auto) => {
    const { kind, text } = postSaveText({
      name: job.name, steps: job.steps, priced, auto, autoOff: job.autoOff, smartRanked: job.smartRanked,
    });
    showToast(kind, text, kind === 'error' ? POST_SAVE_ERROR_TOAST_MS : POST_SAVE_TOAST_MS);
  };
  // { ok } / { ok: false, error }: the Auto-on call never throws here
  const turnAutoOn = (groupId) => updateM.mutateAsync({ id: groupId, autoEnabled: true })
    .then(() => ({ ok: true }), err => ({ ok: false, error: autoOnErrorText(err) }));
  // priced: how the pricing step ended ({ action, result?, reason? }), null when there was none
  const finishPostSave = (job, priced) => {
    setPostSave(p => (p && p.groupId === job.groupId ? null : p));
    if (!shouldTurnAutoOn(job.steps, priced)) {
      showPostSaveToast(job, priced, null);
      return;
    }
    turnAutoOn(job.groupId).then(auto => showPostSaveToast(job, priced, auto));
  };
  const finishPostSaveRef = useRef(finishPostSave);
  finishPostSaveRef.current = finishPostSave;
  const startPostSave = (job) => {
    if (job.groupId == null) {
      showToast('success', `Saved “${job.name}”`, POST_SAVE_TOAST_MS);
      return;
    }
    if (!job.steps.calc) {
      finishPostSave(job, null);
      return;
    }
    setPostSave({ ...job, phase: 'waiting', key: null });
    showToast('success', `Saved “${job.name}” — pricing it now…`, POST_SAVE_TOAST_MS);
  };
  // The saved group on screen and nothing else running: Calculate it. A
  // chain or write started this same commit shows only in the refs, so they
  // are read too. finishPostSaveRef / calculateLadderRef are refs (the
  // latest render's functions).
  useEffect(() => {
    const job = postSave;
    if (job?.phase !== 'waiting') return;
    if (draft || shownGroupId !== job.groupId) {
      finishPostSaveRef.current(job, { action: 'blocked', reason: 'another group was opened first' });
      return;
    }
    if (!shownGroup || ladderM.isPending || ladderWrite.writing || ladderWrite.isBusy() || autoRef.current.armed) return;
    if (ladderBlocked) {
      finishPostSaveRef.current(job, { action: 'blocked', reason: ladderBlocked });
      return;
    }
    setPostSave({ ...job, phase: 'pricing', key: ladderKey });
    calculateLadderRef.current();
  }, [postSave, draft, shownGroupId, shownGroup, ladderM.isPending, ladderWrite.writing, ladderWrite.isBusy, ladderBlocked, ladderKey]);

  // ---- page-level states ----
  const pricingStatus = pricingError?.response?.status;
  const pricingRetryable = !!pricingError && pricingStatus !== 404 && pricingStatus !== 403
    && (pricingStatus === 503 || pricingError.code === 'ECONNABORTED'
      || pricingError.message?.includes('timeout') || !pricingStatus);
  const groupsStatus = groupsQ.error?.response?.status;
  const noAccess = pricingStatus === 403 || groupsStatus === 403;
  const noOwnListings = !!pricing && (pricing.myInventory || []).length === 0;

  let blocker = null;
  if (noAccess) {
    blocker = <div className="la-status error">You do not have access to this event. Ask an admin to assign its performer to you.</div>;
  } else if (pricingError && !pricing) {
    blocker = (
      <div className={`la-status ${pricingRetryable ? 'warn' : 'error'}`}>
        {pricingStatus === 404
          ? 'Pricing data not found for this event.'
          : pricingRetryable
            ? 'Skybox is temporarily unavailable — this usually clears in a moment.'
            : 'Failed to load pricing data.'}
        {pricingRetryable && (
          <button type="button" className="btn btn-secondary btn-sm la-status-action" onClick={() => refetchPricing()}>Retry</button>
        )}
      </div>
    );
  } else if (groupsQ.error && !groupsQ.data) {
    blocker = (
      <div className="la-status error">
        {groupsStatus === 404
          ? 'Lion Auto is not available on this server yet.'
          : (groupsQ.error.response?.data?.error || 'Could not load the groups for this event.')}
        {groupsStatus !== 404 && (
          <button type="button" className="btn btn-secondary btn-sm la-status-action" onClick={() => groupsQ.refetch()}>Retry</button>
        )}
      </div>
    );
  }

  // Sections are picked on the seat map, so a group needs the market and the
  // map; our own listings are optional (they join once bought)
  const canCreate = !!pricing && !!chartData;
  let createHint = null;
  if (pricingLoading) createHint = 'Waiting for the market from Skybox';
  else if (mapLoading) createHint = 'Loading the seat map';
  else if (!chartData) createHint = 'This event has no seat map to pick sections on';

  const atGroupLimit = groups.length >= MAX_GROUPS;

  // The table always lists the selection; the group on screen (the draft's
  // live preview, or the saved group picked, while the selection is its
  // sections) lays its result over it
  let overlay = {};
  if (draft) {
    overlay = {
      title: 'Live preview',
      result: previewResult,
      isLoading: draftHasSections,
      isFetching: !!previewResult && (preview.isFetching || preview.isPlaceholderData || draftInput !== debouncedInput),
      error: draftHasSections ? preview.error : null,
      onRetry: preview.refetch,
      dataUpdatedAt: preview.dataUpdatedAt,
      emptyText: 'Click sections on the map or a zone to build the group: its matches show here as you edit.',
    };
  } else if (shownGroup) {
    overlay = {
      title: shownGroup.name,
      result: savedResult,
      isLoading: matchesQ.isLoading || matchesQ.isFetching,
      isFetching: !!savedResult && matchesQ.isFetching,
      error: matchesQ.error,
      onRetry: matchesQ.refetch,
      dataUpdatedAt: matchesQ.dataUpdatedAt,
      emptyText: 'No match result for this group yet.',
    };
  }

  return (
    <div className="compare-page-vertical la-page">
      <div className="compare-header">
        <a className="back-btn" href={compareHref} title="Back to Compare for this event">&larr; Compare</a>
        <div className="header-title">
          <h1>Lion Auto · {eventName}</h1>
          <div className="event-details">
            {eventDate && <span className="event-date">{formatEventDateTime(eventDate)}</span>}
            {venue && (
              <span className="event-venue">
                {venue}{venueCity && `, ${venueCity}`}{venueState && `, ${venueState}`}
              </span>
            )}
          </div>
        </div>
        <div className="header-actions">
          <button
            className="btn-secondary"
            onClick={handleRefresh}
            disabled={refreshing || pricingLoading || noAccess}
            title="Read fresh market listings from Skybox and match every group again"
          >
            {refreshing ? 'Refreshing…' : '↻ Refresh comps'}
          </button>
          {vividSeatsUrl && (
            <a href={vividSeatsUrl} target="_blank" rel="noopener noreferrer" className="btn-secondary">
              Open VividSeats
            </a>
          )}
        </div>
      </div>

      {toast && <div className={toast.kind === 'success' ? 'success-toast' : 'error-toast'}>{toast.text}</div>}

      <div className="compare-body-split">
        <div className="compare-left-rail">
          <div className="compare-map-section">
            {otherGroupsCss && <style>{otherGroupsCss}</style>}
            {hoverCss && <style>{hoverCss}</style>}
            {mapLoading && <div className="map-loading">Loading map...</div>}
            {svgContent && chartData && !mapLoading && (
              <>
                <CompareSeatMap
                  svgContent={svgContent}
                  chartData={chartData}
                  selectedSections={mapSections}
                  onToggleSection={handleMapToggle}
                  availableSectionIds={availableSectionIds}
                />
                {mapSections.length > 0 && (
                  <div className="selected-sections-bar">
                    <span>
                      {mapSections.length} section{mapSections.length === 1 ? '' : 's'}
                      {editing && <span className="la-muted"> · this group</span>}
                      {shownGroup && <span className="la-muted"> · “{shownGroup.name}”</span>}
                    </span>
                    {!editing && !shownGroup && (
                      <button
                        type="button"
                        className="btn btn-primary btn-sm la-push-right"
                        onClick={startNew}
                        disabled={!canCreate || atGroupLimit}
                        title={atGroupLimit ? `An event holds at most ${MAX_GROUPS} groups` : (createHint || 'Make a pricing group of these sections')}
                      >
                        Save as group
                      </button>
                    )}
                    <button
                      className="clear-btn"
                      onClick={clearSelection}
                      title={editing ? 'Remove every section from this group' : 'Show the whole venue again'}
                    >
                      Clear
                    </button>
                  </div>
                )}
              </>
            )}
            {!svgContent && !mapLoading && <div className="map-placeholder">Map not available</div>}
          </div>

          {svgContent && chartData && !mapLoading && zones.length > 0 && (
            <div className="group-selector-panel">
              <div className="group-selector-header">
                <span>Zones</span>
                <span className="la-muted">{editing ? 'click to add / remove' : 'click to filter'}</span>
              </div>
              <div className="group-selector-list">
                {zones.map(zone => {
                  const n = zone.sections.filter(s => mapSectionIds.has(String(s.id))).length;
                  const all = n > 0 && n === zone.sections.length;
                  const partial = n > 0 && !all;
                  return (
                    <div
                      key={zone.id}
                      className={`group-selector-item${all ? ' selected' : ''}${partial ? ' partial' : ''}`}
                      onClick={() => handleZoneToggle(zone)}
                    >
                      <span className="group-color-dot" style={{ backgroundColor: zone.color }} />
                      <span className="group-checkbox">{all ? '✓' : partial ? '−' : ''}</span>
                      <span className="group-name">{zone.name}</span>
                      <span className="group-section-count">{zone.sections.length}</span>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          <LaCriteriaLegend result={activeResult} sections={mapSections} zones={zones} editing={editing} />
        </div>

        <div className="compare-main-col la-main-col">
          {blocker || (
            <>
              {pricingLoading && <div className="la-status">Loading your listings and the market from Skybox…</div>}
              {noOwnListings && (
                <div className="la-status">
                  You have no listings for this event in Skybox. You can still build groups to watch the
                  market; listings you buy in a group's sections join it by themselves.
                </div>
              )}

              {adminLike && <LaAutoPanel eventId={eventId} canAssignLogin={isSuperAdmin(user?.role)} />}

              <LaFloorsPanel
                key={eventId}
                eventId={eventId}
                followBlocked={draft?.id != null ? 'Save or cancel the open group first' : null}
                canReprice={adminLike}
              />

              <LaSaleRefillPanel key={`sale-refill-${eventId}`} eventId={eventId} />

              <LaGroupList
                groups={groups}
                results={results}
                matchesPending={matchesQ.isFetching}
                selectedId={shownGroup?.id ?? null}
                editing={editing}
                editingId={draft?.id ?? null}
                onSelect={selectGroup}
                onHoverGroup={setHoveredGroupId}
                onEdit={startEdit}
                onDuplicate={startDuplicate}
                onDelete={handleDelete}
                onNew={startNew}
                selectionCount={editing || shownGroup ? 0 : freeSelection.length}
                deletingId={deleteM.isPending ? deleteM.variables : null}
                canCreate={canCreate}
                createHint={createHint}
                isLoading={groupsQ.isLoading}
                canRunAuto={adminLike}
                onRunAuto={handleRunAuto}
                runningAutoId={runAutoM.isPending ? runAutoM.variables : null}
                autoRunBlocked={autoRunBlocked}
                autoRuns={autoRuns}
                onDismissAutoRun={dismissAutoRun}
                autoDue={autoDue}
                listingName={listingName}
                onReviewPending={handleReviewPending}
                reviewingId={reviewM.isPending ? (reviewM.variables?.id ?? null) : null}
                inheritedFloor={inheritedFloor}
                eventId={eventId}
                vividUrl={vividSeatsUrl}
              />

              {draft && (
                <LaGroupEditor
                  key={draft.id ?? 'new'}
                  draft={draft}
                  onChange={patchDraft}
                  resolved={previewResult?.resolved || null}
                  anchorListing={previewResult?.anchor?.listing || null}
                  chartSections={chartData?.sections || null}
                  rowOptions={rowOptions}
                  onSave={handleSave}
                  onCancel={cancelEdit}
                  saving={saving}
                  saveBlocked={postSave ? `Wait: “${postSave.name}” is still being priced after its save` : null}
                  saveError={saveError}
                  notice={draftNotice}
                  dirty={draftDirty}
                  initialAdvancedOpen={draftAdvancedOpen}
                  canEnableAuto={adminLike}
                  savedGroup={draft.id != null ? groups.find(g => g.id === draft.id) || null : null}
                  autoRunner={autoRunner}
                  saleRefill={saleRefill}
                  inheritedFloor={inheritedFloor}
                />
              )}

              {rankedCount > 0 && (
                <LaLadderBar
                  ranked={rankedCount}
                  offset={ladderSpec.criteria.ladderOffset}
                  step={ladderSpec.criteria.ladderStep}
                  floorPct={ladderSpec.floorPct}
                  floorSource={ladderFloor.source}
                  performerName={inheritedFloor?.performerName ?? null}
                  floorChangedText={ladderFloorChangedText(ladderData, ladderFloorPct)}
                  hold={ladderHold}
                  onCalculate={calculateLadder}
                  calculating={runningHere && !pendingLadder.resume}
                  resume={ladderResumeAt}
                  onContinue={continueLadder}
                  continuing={runningHere && !!pendingLadder.resume}
                  autoContinuing={autoContinuing}
                  blockedReason={ladderBlocked}
                  data={ladderData}
                  error={shownLadder?.error || null}
                  receivedAt={shownLadder?.receivedAt}
                  write={shownWrite}
                  noCompPct={ladderSpec.criteria.noCompDropPct}
                  zoneFallback={ladderSpec.criteria.compZoneFallback === true}
                  autoGroup={groups.find(g => g.id === ladderSpec.groupId) || null}
                  autoDryRun={autoStatus ? isDryRun(autoStatus) : null}
                />
              )}

              {pricing && (
                <LaListingsTable
                  pricing={tablePricing}
                  chartData={chartData}
                  canSeeCost={!!user?.canSeeCost}
                  selectedSections={mapSections}
                  groupMembership={groupMembership}
                  groupsLoading={groupsLoading}
                  currentGroup={currentGroupName}
                  ranking={ranking}
                  overrides={overrides}
                  showAllOwn={editing && showAllOwn}
                  onShowAllOwnChange={setShowAllOwn}
                  ladder={ladderData}
                  staleLCount={lStaleIds.size}
                  onCheckL={lPollNow}
                  {...overlay}
                />
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}

export default LionAuto;
